/**
 * Pipeline Executor — processes an ordered array of composable operations.
 * Replaces the monolithic switch statement in query-router.js.
 *
 * Each operation reads from and writes to a shared context object (`ctx`).
 * The context carries both infrastructure references (services) and
 * accumulated data results from previous pipeline steps.
 *
 * Phase 1: Executes navigate + terrain ops via pipeline.
 * Phase 2+: All handlers extracted into ops/ modules.
 */
import { registry } from './operation-registry.js'

/**
 * Validate a pipeline before execution.
 * Checks that ops exist and that data dependencies are satisfiable.
 * Returns { valid: true } or { valid: false, error: string }.
 */
export function validatePipeline(pipeline) {
  if (!Array.isArray(pipeline) || pipeline.length === 0) {
    return { valid: false, error: 'Pipeline must be a non-empty array of operations.' }
  }

  // Track which context keys will be available at each step
  // Start with keys that are always available from services
  const availableKeys = new Set([
    'services',
    'globe',
    'duckdb',
    'layers',
    'legends',
    'lastSQL',
    'resultCount',
    'resultRow',
  ])

  for (let i = 0; i < pipeline.length; i++) {
    const step = pipeline[i]

    if (!step || typeof step.op !== 'string') {
      return { valid: false, error: `Step ${i}: missing or invalid "op" field.` }
    }

    const handler = registry.get(step.op)
    if (!handler) {
      return { valid: false, error: `Step ${i}: unknown operation "${step.op}".` }
    }

    // Check if required inputs are available
    if (handler.meta?.inputs) {
      for (const input of handler.meta.inputs) {
        // Strip "ctx." prefix for matching
        const key = input.replace(/^ctx\./, '')
        // Inputs with $ prefix are dynamic references resolved at runtime
        // (e.g., "$poi_count" refers to a previous step's output)
        // We can't statically validate these, so skip them
        if (key.startsWith('$')) continue

        // "services.*" are always available
        if (key.startsWith('services.')) continue

        // For non-service inputs, check if a previous step provides them
        // This is a soft check — some inputs are optional
        // We only warn, not fail, since operations handle missing data gracefully
      }
    }

    // Register this step's outputs as available for subsequent steps
    if (handler.meta?.outputs) {
      for (const output of handler.meta.outputs) {
        const key = output.replace(/^ctx\./, '')
        availableKeys.add(key)
      }
    }
  }

  return { valid: true }
}

/**
 * Create a fresh pipeline context.
 * Services are infrastructure references shared across all operations.
 * Data properties accumulate as operations execute.
 */
export function createContext({
  animator,
  layers,
  speech,
  locationIndex,
  countriesData,
  adminBoundaries,
  adminPMTiles,
  duckdb,
  onResultUpdate,
}) {
  return {
    // Infrastructure services — read-only references for operations
    services: {
      animator,
      layers,
      speech,
      locationIndex,
      countriesData,
      adminBoundaries,
      adminPMTiles,
      duckdb,
      onResultUpdate,
    },

    // Accumulated data from pipeline steps
    boundaries: null, // GeoJSON from load_boundaries
    indicators: null, // Rows from load_indicators
    poi_counts: null, // Rows from count_pois
    cities: null, // Rows from query_cities
    query_result: null, // Rows from query_duckdb
    normalized: null, // Rows from normalize
    aggregated: null, // Rows from aggregate
    temporal: null, // Rows from temporal_compare
    filtered: null, // Rows from filter
    ranked: null, // Rows from rank

    // Render state
    // NOTE: intentionally no top-level `layers` field — it would collide
    // with the `layers` service (LayerManager) if a handler destructured
    // `const { layers } = ctx` by mistake, silently swapping the manager
    // for an empty array and crashing with
    // "layers.showChoropleth is not a function". Handlers must read the
    // manager via `ctx.services.layers`. See CL-041.
    legends: [], // Legend configs for multi-legend rendering

    // Result tracking (mirrors QueryRouter._last* state)
    lastSQL: null,
    resultCount: null,
    resultRow: null,
    spokeDuringRoute: false,
  }
}

/**
 * Execute a pipeline: an ordered array of operation steps.
 * Each step is { op: string, ...params }.
 * Returns the final context after all steps complete.
 */
export async function executePipeline(pipeline, ctx) {
  // Pre-flight validation
  const validation = validatePipeline(pipeline)
  if (!validation.valid) {
    console.error(`[PipelineExecutor] Validation failed: ${validation.error}`)
    throw new Error(`Pipeline validation failed: ${validation.error}`)
  }

  console.log(
    `[PipelineExecutor] Executing ${pipeline.length}-step pipeline:`,
    pipeline.map((s) => s.op).join(' → ')
  )

  for (let i = 0; i < pipeline.length; i++) {
    const step = pipeline[i]
    const handler = registry.get(step.op)

    // Handler existence already verified by validatePipeline, but guard anyway
    if (!handler) {
      console.error(`[PipelineExecutor] Step ${i}: unknown op "${step.op}" — skipping`)
      continue
    }

    try {
      console.log(`[PipelineExecutor] Step ${i}: ${step.op}`, step)
      // CL-165 (roadmap H3.2): snapshot layer ids around the op so whatever
      // it renders gets registered as a NAMED, controllable stack entry —
      // no render method needs to know about the stack.
      const lm = ctx.services?.layers
      const before = lm?.snapshotLayerIds?.()
      await handler.execute(step, ctx)
      if (before) lm.registerStackFromDiff?.(step, before)
    } catch (err) {
      console.error(`[PipelineExecutor] Step ${i} (${step.op}) failed:`, err)

      // Per the architecture doc: "The pipeline executor must continue
      // processing remaining steps even if one step returns empty data."
      // However, we distinguish between "empty data" (continue) and
      // "hard error" (stop). Operations that return empty data should
      // handle it gracefully internally rather than throwing.
      // A thrown error means something is genuinely broken — stop.
      throw err
    }
  }

  // After all steps: render multi-legend if any legends accumulated
  if (ctx.legends.length > 0) {
    _renderMultiLegend(ctx)
  }

  return ctx
}

/**
 * Render accumulated legends.
 * Phase 1: passes the last legend to the existing single-legend component.
 * Phase 3+: will use multi-legend component for stacked display.
 */
function _renderMultiLegend(ctx) {
  // For now, show the most recent legend via the existing legendCallback
  // Multi-legend component will be built in Phase 3
  const lastLegend = ctx.legends[ctx.legends.length - 1]
  if (lastLegend && ctx.services.layers?.legendCallback) {
    ctx.services.layers.legendCallback(lastLegend)
  }
}
